import type { ResumeDocument } from '@careeropt/schema';
import {
  applyTemplateDisplayOrder,
  effectiveAccent,
  type ResumeTemplateFamily,
} from '../../lib/resumeTemplateRegistry';

// =============================================================================
// ResumeView (throughline) — the SAME résumé format apps/web's ResumeView.tsx
// renders, ported 1:1. This is a re-skin, not a redesign (Jason, direct: "match
// the EXISTING résumé format EXACTLY as it was built before"): every element,
// class name, and layout decision below mirrors apps/web/src/components/
// resume/ResumeView.tsx line-for-line. The ONLY change is the color system —
// apps/web's .resume-doc hardcodes five hex values via inline CSS custom
// properties (--ink/--muted/--rule/--accent); this version routes the exact
// same four visual roles through throughline's live Sage Teal tokens
// (--tl-ink / --tl-ink-soft / --tl-line / --tl-accent) so the résumé honors
// whichever of the two live themes (sage-teal / ember-gray) the user has
// selected, instead of being locked to apps/web's fixed light-CV palette.
//
// THIN PRESENTATION / NO INVENTION — same law as the source: doc.sections is
// rendered by exhaustive kind switch, never reshaped, ordered, or synthesised
// here. The backend (ResumeComposerService) is the only author of content;
// this component only lays it out.
// =============================================================================

// Exported (additive) so a future per-era fragment component (mirroring apps/
// web's RoleResumeSection) can reuse the identical .rd-* styling without
// duplicating this block, exactly as apps/web's own RESUME_CSS export is used.
export const RESUME_CSS = `
.resume-doc {
  background: var(--tl-surf); color: var(--tl-ink);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: 10.5pt; line-height: 1.42;
  width: 210mm; min-height: 297mm; box-sizing: border-box;
  padding: 16mm 18mm; margin: 0 auto;
  /* OVERFLOW FIX (Jason: "text is not wrapping so it looks to open up the
     iframe and makes the rendering of the resume unreadable and clunky") —
     a long unbroken token (a raw URL in a link, an unabbreviated company/
     title, a long KPI clause) previously had no wrap rule anywhere in this
     sheet, so it forced its containing box wider than the fixed 210mm and
     blew out through CorpusResumePanel's scale-transform preview AND the
     print clone. overflow-wrap holds the fixed page width for every text
     node in this document, on screen and on paper (the print media block
     below does not override or need to override this — it is medium-
     agnostic, so it is declared once here, not duplicated per media). */
  overflow-wrap: anywhere;
  word-break: break-word;
}
.rd-name { font-size: 22pt; font-weight: 700; letter-spacing: -0.01em; margin: 0; overflow-wrap: anywhere; }
.rd-contact { color: var(--tl-ink-soft); font-size: 9.5pt; margin-top: 3px; overflow-wrap: anywhere; }
.rd-contact a { color: var(--tl-ink-soft); text-decoration: none; }
.rd-section { margin-top: 16px; }
.rd-section-h {
  font-size: 9pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--tl-accent); border-bottom: 1px solid var(--tl-line); padding-bottom: 3px; margin: 0 0 8px;
}
.rd-profile { margin: 0; }
/* PER-ERA CHUNKING (Jason: "we need to chunk it so that each era... is kinda
   set") — a hairline rule + its own top padding gives every role its own
   visually-bounded block (the same --tl-line rule .rd-section-h already uses
   for a section boundary, applied per-role instead of per-section) so
   scanning the document reads as a sequence of distinct eras, not one
   unbroken scroll of bullets. The first role in a section has no rule/role
   above it to separate from, so it opts out via :first-child. break-inside
   keeps one era from splitting across a printed page (screen ignores
   break-inside; it is a print-only concern, safe as a no-op on screen). */
.rd-role {
  margin-bottom: 11px; padding-top: 11px; border-top: 1px solid var(--tl-line);
  break-inside: avoid; page-break-inside: avoid;
  min-width: 0;
}
.rd-role:first-child { padding-top: 0; border-top: none; }
.rd-role-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; flex-wrap: wrap; min-width: 0; }
.rd-role-title { font-weight: 700; overflow-wrap: anywhere; }
.rd-role-co { font-weight: 600; overflow-wrap: anywhere; }
.rd-role-dates { color: var(--tl-ink-soft); font-size: 9.5pt; white-space: nowrap; }
.rd-role-desc { color: var(--tl-ink-soft); font-style: italic; font-size: 9.5pt; margin: 1px 0 4px; overflow-wrap: anywhere; }
.rd-bullets { margin: 4px 0 0; padding: 0; list-style: none; }
.rd-bullet { position: relative; padding-left: 14px; margin-bottom: 3px; overflow-wrap: anywhere; }
.rd-bullet::before { content: '▪'; position: absolute; left: 0; color: var(--tl-accent); font-size: 8pt; top: 1px; }
/* THE ESCAPING ELEMENT (RESUME-POLISH lane): white-space:nowrap here forbids
   ANY line break inside the KPI chip, so a long parenthetical KPI clause — the
   owner's live example: "(5,000+ movements daily across 138+ locations in 16
   cities; 48 teams; 3 countries Daily movements / locations / host cities)" —
   forces its containing .rd-bullet (and the whole .resume-doc) wider than the
   fixed 210mm page, exactly the R232 defect that pass's overflow-wrap sweep
   did not reach (it added overflow-wrap to every OTHER text-bearing selector
   in this sheet but left this one line's nowrap standing). Dropped: a KPI
   chip is ordinary inline text like every other bullet fragment, and now
   wraps like one (overflow-wrap: anywhere, matching the sheet's own house
   rule for every other rd-* text node). */
.rd-kpi { color: var(--tl-accent); font-weight: 600; overflow-wrap: anywhere; }
.rd-skill-group { margin-bottom: 3px; overflow-wrap: anywhere; }
.rd-skill-cat { font-weight: 600; }
.rd-edu-item, .rd-cert-item { margin-bottom: 3px; overflow-wrap: anywhere; }
.rd-edu-inst { font-weight: 600; }
.rd-muted { color: var(--tl-ink-soft); }
.rd-sc-crit { font-weight: 600; margin: 8px 0 2px; overflow-wrap: anywhere; }
.rd-sc-body { margin: 0 0 6px; white-space: pre-wrap; overflow-wrap: anywhere; }
.rd-sc-empty { color: var(--tl-ink-soft); font-style: italic; }
@media print {
  @page { size: A4; margin: 0; }
  .resume-doc { width: 100%; min-height: auto; box-shadow: none; margin: 0; padding: 14mm 16mm; }
  .rd-role { break-inside: avoid; page-break-inside: avoid; }
  .rd-section { break-inside: avoid-page; }
}
/* TEMPLATES-P2 (docs/design/resume-template-library-2026-07-05.md section 4/10) --
   additive template-variant rules, scoped by [data-template-variant] so the
   base ruleset above is completely untouched for every caller that omits
   ResumeView's new family prop (the default, byte-identical render). The
   accent colour itself is threaded via an inline --tl-accent OVERRIDE on the
   root node (see the component below), not a rule here -- only the two
   variant-specific STRUCTURAL differences (Counter-Ready's larger counter-
   legible type, Modern Professional's restrained header rule) live as CSS. */
.resume-doc[data-template-variant="counter-ready"] { font-size: 12pt; }
.resume-doc[data-template-variant="counter-ready"] .rd-name { font-size: 24pt; }
.resume-doc[data-template-variant="modern-professional"] .rd-section-h { border-bottom-width: 1.5px; }
`;

function fmtDate(d?: string | null): string {
  if (!d) return '';
  // YYYY-MM(-DD) → "Mon YYYY"
  const m = /^(\d{4})-(\d{2})/.exec(d);
  if (!m) return d;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m[2]) - 1] ?? ''} ${m[1]}`.trim();
}

function roleDates(start: string, end: string | null, isCurrent: boolean): string {
  const s = fmtDate(start);
  const e = isCurrent ? 'Present' : fmtDate(end);
  return [s, e].filter(Boolean).join(' – ');
}

export const RESUME_VIEW_DOM_ID = 'tl-resume-doc-root';

/**
 * `family`/`accentKey` are OPTIONAL (TEMPLATES-P2). Omitted: byte-identical
 * to before this lane — the original section order, --tl-accent untouched
 * (the app theme's own accent, exactly as today). Named: applies the
 * family's display section order (§3's view swap — e.g. Recent Graduate's
 * education-first sequence), the resolved accent colour as a scoped
 * --tl-accent OVERRIDE on this instance's root node ONLY (never a global
 * theme change — the rest of the app keeps its own accent), and the
 * variant-specific structural CSS rule above (Counter-Ready/Modern
 * Professional). The SAME three axes resumeToDocx.ts and resumeTemplatePdf.tsx
 * apply, so screen/docx/PDF agree on what a given family looks like.
 */
export function ResumeView({
  doc,
  family,
  accentKey,
}: {
  doc: ResumeDocument;
  family?: ResumeTemplateFamily;
  accentKey?: string | null;
}) {
  const displayDoc = family ? applyTemplateDisplayOrder(doc, family) : doc;
  const { contact } = displayDoc;
  const contactBits = [contact.location, contact.email, contact.phone].filter(Boolean);
  const accentOverride = family ? { ['--tl-accent' as string]: effectiveAccent(family, accentKey).hex } : undefined;
  return (
    <div
      className="resume-doc"
      id={RESUME_VIEW_DOM_ID}
      data-template-variant={family}
      style={accentOverride}
    >
      <style>{RESUME_CSS}</style>

      <header>
        <h1 className="rd-name">{contact.name || 'Your Name'}</h1>
        <div className="rd-contact">
          {contactBits.join('  ·  ')}
          {contact.links?.length ? (
            <>
              {contactBits.length ? '  ·  ' : ''}
              {contact.links.map((l, i) => (
                <span key={i}>
                  {i > 0 ? '  ·  ' : ''}
                  <a href={l.url}>{l.label}</a>
                </span>
              ))}
            </>
          ) : null}
        </div>
      </header>

      {displayDoc.sections.map((section, si) => {
        switch (section.kind) {
          case 'profile':
            return (
              <section className="rd-section" key={si}>
                <h2 className="rd-section-h">{section.heading}</h2>
                <p className="rd-profile">{section.body}</p>
              </section>
            );
          case 'experience':
            return (
              <section className="rd-section" key={si}>
                <h2 className="rd-section-h">{section.heading}</h2>
                {section.roles.map((r, ri) => (
                  <div className="rd-role" key={ri}>
                    <div className="rd-role-head">
                      <span>
                        <span className="rd-role-title">{r.title}</span>
                        {', '}
                        <span className="rd-role-co">{r.company}</span>
                        {r.location ? <span className="rd-muted">{`  ${r.location}`}</span> : null}
                      </span>
                      <span className="rd-role-dates">
                        {roleDates(r.startDate, r.endDate, r.isCurrent)}
                      </span>
                    </div>
                    {r.descriptor ? <div className="rd-role-desc">{r.descriptor}</div> : null}
                    {r.bullets.length ? (
                      <ul className="rd-bullets">
                        {r.bullets.map((b, bi) => (
                          <li className="rd-bullet" key={bi}>
                            {b.text}
                            {b.kpi ? <span className="rd-kpi">{`  (${b.kpi})`}</span> : null}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}
              </section>
            );
          case 'skills':
            return (
              <section className="rd-section" key={si}>
                <h2 className="rd-section-h">{section.heading}</h2>
                {section.groups.map((g, gi) => (
                  <div className="rd-skill-group" key={gi}>
                    <span className="rd-skill-cat">{g.category}: </span>
                    <span>{g.items.join(', ')}</span>
                  </div>
                ))}
              </section>
            );
          case 'education':
            return (
              <section className="rd-section" key={si}>
                <h2 className="rd-section-h">{section.heading}</h2>
                {section.items.map((e, ei) => (
                  <div className="rd-edu-item" key={ei}>
                    <span className="rd-edu-inst">{e.institution}</span>
                    {e.credential ? <span>{` — ${e.credential}`}</span> : null}
                    {e.field ? <span className="rd-muted">{`, ${e.field}`}</span> : null}
                    {e.startDate || e.endDate ? (
                      <span className="rd-muted">{`  (${[fmtDate(e.startDate), fmtDate(e.endDate)].filter(Boolean).join(' – ')})`}</span>
                    ) : null}
                  </div>
                ))}
              </section>
            );
          case 'certifications':
            return (
              <section className="rd-section" key={si}>
                <h2 className="rd-section-h">{section.heading}</h2>
                {section.items.map((c, ci) => (
                  <div className="rd-cert-item" key={ci}>
                    <span className="rd-edu-inst">{c.name}</span>
                    {c.issuer ? <span className="rd-muted">{` — ${c.issuer}`}</span> : null}
                    {c.issuedDate ? <span className="rd-muted">{`  (${fmtDate(c.issuedDate)})`}</span> : null}
                  </div>
                ))}
              </section>
            );
          case 'selection_criteria':
            return (
              <section className="rd-section" key={si}>
                <h2 className="rd-section-h">{section.heading}</h2>
                {section.responses.map((r, rci) => (
                  <div key={rci}>
                    <div className="rd-sc-crit">{r.criterion}</div>
                    {r.body ? (
                      <p className="rd-sc-body">{r.body}</p>
                    ) : (
                      <p className="rd-sc-body rd-sc-empty">Draft your response to this criterion.</p>
                    )}
                  </div>
                ))}
              </section>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
